import { useState, useRef } from 'react';
import './image-upload.scss';
import { compressImages } from '../../utils/imageCompression';

interface ImageUploadProps {
  onImagesUploaded: (files: File[]) => void | Promise<void>;
  maxImages?: number;
  title?: string; // 🔵 NEW
}

export default function ImageUpload({ 
  onImagesUploaded, 
  maxImages = 5,
  title = "رفع الصور" // 🔵 default fallback
}: ImageUploadProps) {

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (uploadedFiles.length + files.length > maxImages) {
      alert(`يمكنك رفع ${maxImages} صور كحد أقصى`);
      return;
    }

    // Compress images automatically
    try {
      const compressedFiles = await compressImages(files, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1920,
        quality: 0.85,
      });

      const newFiles = [...uploadedFiles, ...compressedFiles];
      setUploadedFiles(newFiles);

      // Generate previews from compressed files
      const newPreviews = compressedFiles.map(file => URL.createObjectURL(file));
      setPreviews([...previews, ...newPreviews]);

      // Notify parent with compressed files
      await onImagesUploaded(newFiles);
    } catch (error) {
      console.error('❌ Error compressing images:', error);
      // Fallback: use original files if compression fails
      const newFiles = [...uploadedFiles, ...files];
      setUploadedFiles(newFiles);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setPreviews([...previews, ...newPreviews]);
      await onImagesUploaded(newFiles);
    }

    // Reset input to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const removeImage = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    // Revoke URL to free memory
    URL.revokeObjectURL(previews[index]);
    
    setUploadedFiles(newFiles);
    setPreviews(newPreviews);
    onImagesUploaded(newFiles);
  };

  return (
    <div className="image-upload-container">
      <div className="upload-header">
        <h3>{title}</h3> {/* 🔵 Replace old static text */}
        <p className="upload-count">{uploadedFiles.length} / {maxImages}</p>
      </div>

      <div className="upload-actions">
        <button 
          className="upload-button gallery"
          onClick={() => galleryInputRef.current?.click()}
          disabled={uploadedFiles.length >= maxImages}
        >
          <span className="material-symbols-outlined">photo_library</span>
          اختر من المعرض
        </button>

        <button 
          className="upload-button camera"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploadedFiles.length >= maxImages}
        >
          <span className="material-symbols-outlined">photo_camera</span>
          التقط صورة
        </button>
      </div>

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple={maxImages > 1}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={maxImages > 1}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {previews.length > 0 && (
        <div className="preview-grid">
          {previews.map((preview, index) => (
            <div key={index} className="preview-item">
              <img src={preview} alt={`Preview ${index + 1}`} />
              <button 
                className="remove-button"
                onClick={() => removeImage(index)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
